import {
  createContext,
  useContext,
  useMemo,
  type FC,
  type ReactNode
} from "react";

import { useCaseFilesystem } from "../../caseFiles/hooks/useCaseFilesystem";
import {
  useCompatMigration,
  type CompatMigration
} from "../compat/useCompatMigration";
import { useCaseIde, useOnInvalidate } from "../context/CaseIdeContext";
import {
  useCaseWorkspace,
  type CaseWorkspace
} from "../workspace/useCaseWorkspace";

type Filesystem = ReturnType<typeof useCaseFilesystem>;

export type CaseData = {
  caseId: string | null;
  fs: Filesystem;
  workspace: CaseWorkspace;
  /** The one-time legacy upgrade that runs the first time a case is opened. */
  compat: CompatMigration;
};

const CaseDataContext = createContext<CaseData | null>(null);

type Props = { caseId: string | null; children: ReactNode };

/**
 * One fetch of the filesystem and the workspace for the whole IDE. The trees,
 * the editor panes and the chat all read from here, so a `data-invalidate`
 * refreshes every view at once.
 */
export const CaseDataProvider: FC<Props> = ({ caseId, children }) => {
  const { individualId } = useCaseIde();
  const fs = useCaseFilesystem(individualId);
  const workspace = useCaseWorkspace(individualId, caseId);
  const compat = useCompatMigration(individualId);

  useOnInvalidate(["fs", "autocategorize"], fs.refresh);

  const value = useMemo<CaseData>(
    () => ({ caseId, fs, workspace, compat }),
    [caseId, fs, workspace, compat]
  );
  return (
    <CaseDataContext.Provider value={value}>
      {children}
    </CaseDataContext.Provider>
  );
};

export function useCaseData(): CaseData {
  const ctx = useContext(CaseDataContext);
  if (!ctx) throw new Error("useCaseData must be used within CaseDataProvider");
  return ctx;
}

/** Null outside the IDE (no files or workspace to mention). */
export function useCaseDataOptional(): CaseData | null {
  return useContext(CaseDataContext);
}
